import { useFormContext, useWatch } from 'react-hook-form';
import { Section, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useMemo } from 'react';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { ItemDetailsFormData } from './types';
import { useItemDetailsForm } from './useItemDetailForm';
import { useMerchHierarchyOptions } from '../../hooks/useMerchHierarchyOptions.hooks';

export function CategoryDetailsSection() {
  const { control, watch, setValue } = useFormContext<ItemDetailsFormData>();
  const { makeOptionsFromMetaData } = useItemDetailsForm();

  const currentDivision = watch('division');
  const currentCategory = watch('category');
  const currentClass = watch('class');
  const currentDepartment = watch('department');

  const [currentDetail1, currentDetail2, currentDetail3] = useWatch({
    control,
    name: ['detail1', 'detail2', 'detail3'],
  });

  const {
    departmentOptions,
    categoriesOptions,
    divisionsOptions,
    classOptions,
  } = useMerchHierarchyOptions({
    currentDivision,
    currentDepartment,
    currentCategory,
    currentClass,
    setDepartmentValue: (value) => {
      setValue('department', value);
    },
    setCategoryValue: (value) => {
      setValue('category', value);
    },
    setClassValue: (value) => {
      setValue('class', value);
    },
  });

  const detail3Options = useMemo(
    () => makeOptionsFromMetaData('ns_master_details', 'Detail 3'),
    [makeOptionsFromMetaData],
  );
  const detail2Options = useMemo(
    () => makeOptionsFromMetaData('ns_master_details', 'Detail 2'),
    [makeOptionsFromMetaData],
  );
  const detail1Options = useMemo(
    () => makeOptionsFromMetaData('ns_master_details', 'Detail 1'),
    [makeOptionsFromMetaData],
  );

  useEffect(() => {
    if (
      currentDetail2 &&
      detail1Options.length > 0 &&
      !detail1Options.some((opt) => opt.value === currentDetail2)
    ) {
      setValue('detail2', '');
    }
  }, [detail1Options]);

  useEffect(() => {
    if (
      currentDetail3 &&
      detail2Options.length > 0 &&
      !detail2Options.some((opt) => opt.value === currentDetail3)
    ) {
      setValue('detail3', '');
    }
  }, [detail2Options]);

  useEffect(() => {
    if (
      currentDetail1 &&
      currentDetail1?.length > 0 &&
      detail3Options.length > 0
    ) {
      const updated = currentDetail1.filter((detail) =>
        detail3Options.some((opt) => opt.value === detail),
      );
      setValue('detail1', updated);
    }
  }, [detail3Options]);

  return (
    <Section
      icon="list/list-search"
      title="Category Details"
      color="yellow"
      className="w-full flex flex-col"
    >
      <Stack direction="col" gap="md">
        <ControlledFormInput
          name="styleNumber"
          label="Style Number"
          control={control}
          inputType="text"
          required
          inputProps={{ className: 'w-full' }}
        />
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="jrVendorReference"
            label="JR Vendor Reference #"
            control={control}
            inputType="text"
            inputProps={{ className: 'w-full' }}
          />
          <ControlledFormInput
            name="plusVendorReference"
            label="Plus Vendor Reference #"
            control={control}
            inputType="text"
            inputProps={{ className: 'w-full' }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="division"
            label="Division"
            control={control}
            inputType="multiselect"
            required
            disabled={divisionsOptions.length <= 0}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: divisionsOptions,
            }}
          />
          <ControlledFormInput
            name="department"
            label="Department"
            control={control}
            inputType="multiselect"
            required
            disabled={departmentOptions.length <= 0}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: departmentOptions,
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="category"
            label="Category"
            control={control}
            inputType="multiselect"
            required
            disabled={categoriesOptions.length <= 0}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: categoriesOptions,
            }}
          />
          <ControlledFormInput
            name="class"
            label="Class"
            control={control}
            inputType="multiselect"
            required
            disabled={classOptions.length <= 0}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: classOptions,
            }}
          />
        </Stack>
        <ControlledFormInput
          name="fashionnovaExclusive"
          label="Fashionnova Exclusive"
          control={control}
          inputType="boolean"
          disabled={false}
          rules={{
            validate: (value) => value === true || value === false,
          }}
          inputProps={{
            labelLeft: 'Exclusive',
            labelRight: 'Not Exclusive',
          }}
        />
      </Stack>
    </Section>
  );
}
